import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { MapPin, Users, Calendar, CreditCard as Edit, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { ActionMenu } from './ui/ActionMenu';
import { formatDistance } from '../utils/distance';
import type { Event } from '../types/database';
import { dateLocales } from '../i18n';

interface EventPanelProps {
  event: Event & {
    band?: {
      name: string;
      avatar_url: string | null;
      slug: string;
    };
    bands?: {
      name: string;
      avatar_url: string | null;
      slug: string;
    }[];
    distance?: number;
  };
  bandSlug?: string;
  nearMeEnabled?: boolean;
  showActions?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function EventPanel({ event, bandSlug, nearMeEnabled = false, showActions, onEdit, onDelete }: EventPanelProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = dateLocales[i18n.language as keyof typeof dateLocales] || dateLocales.en;
  
  const handleClick = (e: React.MouseEvent) => {
    // Only navigate if the click wasn't on the action menu
    if (!(e.target as HTMLElement).closest('.action-menu')) {
      navigate(`/bands/${bandSlug || event.band?.slug}/events/${event.slug}`);
    }
  };

  return (
    <motion.div 
      onClick={handleClick}
      className="block w-full h-full relative group cursor-pointer"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      whileHover={{ scale: 1.02 }}
    >
      <div className="panel hover:bg-gray-custom/20">
        {showActions && (
          <div className="absolute top-4 right-4 md:opacity-0 group-hover:opacity-100 transition-opacity action-menu">
            <ActionMenu
              items={[
                {
                  label: 'Edit Event',
                  icon: <Edit className="w-4 h-4" />,
                  onClick: onEdit,
                },
                {
                  label: 'Delete Event',
                  icon: <Trash2 className="w-4 h-4" />,
                  onClick: onDelete,
                  className: 'text-red-500 hover:text-red-500',
                },
              ]}
            />
          </div>
        )}

        <motion.div 
          className="flex justify-start items-center gap-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <motion.div 
            className="bg-primary rounded-lg h-16 w-16 flex items-center justify-center"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <Calendar className="w-10 h-10 text-light" />
          </motion.div>
          <motion.div 
            className="text-left"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="font-tilt-warp text-4xl text-light">
              {format(new Date(event.event_date), 'dd. MMM', { locale })}
            </div>
            <div className="text-xl text-light/80">
              {format(new Date(event.event_date), 'EE, HH:mm', { locale })} Uhr
            </div>
          </motion.div>
        </motion.div>
        <motion.div 
          className="mt-4"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h3 className="font-tilt-warp text-xl text-light mb-2">{event.title}</h3>
          <div className="flex flex-col gap-3 text-sm">
            <motion.div 
              className="flex items-center justify-between text-light/60"
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                <span>{event.location}</span>
              </div>
              {nearMeEnabled && event.distance !== undefined && (
                <span className="text-primary ml-2">
                  {formatDistance(event.distance)}
                </span>
              )}
            </motion.div>
          </div>
          {event.bands && event.bands.length > 0 ? (
            <motion.div
              className="mt-4"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <div className="flex flex-wrap gap-2">
                {event.bands.map((band, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-gray-custom/10 px-3 py-1.5 rounded-full">
                    <div className="w-6 h-6 bg-gray-custom/20 flex items-center justify-center overflow-hidden rounded-full">
                      {band.avatar_url ? (
                        <img
                          src={band.avatar_url}
                          alt={band.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <Users className="w-3 h-3 text-light/60" />
                      )}
                    </div>
                    <span className="text-sm text-light">{band.name}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ) : event.band && (
            <motion.div
              className="flex items-center space-x-4 mt-4"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <div className="w-12 h-12 bg-gray-custom/10 flex items-center justify-center overflow-hidden">
                {event.band.avatar_url ? (
                  <img
                    src={event.band.avatar_url}
                    alt={event.band.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <Users className="w-4 h-4 text-light/60" />
                )}
              </div>
              <div>
                <h3 className="font-medium text-light">{event.band.name}</h3>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}